<script setup lang="ts">
import type { ReviewResponse } from '@/types'

const props = defineProps<{
  clinicSlug: string
  clinicId: string
  clinicName: string
  triggerOpen?: number
}>()

const toast = useToast()
const { isAuthenticated, isAuthReady, userId } = useAuth()
const { isClinicOwner } = useUser()
const { capture } = useAnalytics()
const { createReview, deleteReview, isLoading: isDeleting } = useReviews(props.clinicId)
const { get: getPendingReview, hasPendingForClinic, clear: clearPendingReview } = usePendingReview()

const page = ref(1)
const limit = 10
const offset = computed(() => (page.value - 1) * limit)

interface ReviewsApiResponse {
  success: boolean
  data: ReviewResponse[]
  total: number
  limit: number
  offset: number
  averageRating: number | null
  distribution: Record<number, number>
}

const { data: reviewsData, pending, refresh } = await useLazyAsyncData(
  `reviews-${props.clinicSlug}`,
  () =>
    $fetch<ReviewsApiResponse>(`/api/clinics/${props.clinicSlug}/reviews`, {
      query: {
        limit,
        offset: offset.value,
      },
    }),
  {
    watch: [offset],
  }
)

const reviews = computed(() => reviewsData.value?.data || [])
const total = computed(() => reviewsData.value?.total ?? 0)
const totalPages = computed(() => Math.ceil(total.value / limit))
const averageRating = computed(() => reviewsData.value?.averageRating ?? null)
const distribution = computed(() => reviewsData.value?.distribution ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 })
const ratedCount = computed(() => {
  const d = distribution.value
  return (d[1] ?? 0) + (d[2] ?? 0) + (d[3] ?? 0) + (d[4] ?? 0) + (d[5] ?? 0)
})

function distributionPercent(star: number): number {
  if (ratedCount.value === 0) return 0
  return ((distribution.value[star] ?? 0) / ratedCount.value) * 100
}

function formatAverage(value: number): string {
  return value.toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

const userReview = computed(() => {
  if (!userId.value) return null
  return reviews.value.find((r) => r.user.id === userId.value) || null
})

const hasUserReviewed = computed(() => !!userReview.value)

const isReviewFormOpen = ref(false)
const editingReview = ref<ReviewResponse | null>(null)
const isDeleteConfirmOpen = ref(false)
const reviewToDelete = ref<ReviewResponse | null>(null)

function openReviewForm(review?: ReviewResponse) {
  capture('review_form_opened', { clinic_id: props.clinicId, is_authenticated: isAuthenticated.value })
  editingReview.value = review || null
  isReviewFormOpen.value = true
}

function handleReviewSuccess() {
  isReviewFormOpen.value = false
  editingReview.value = null
  refresh()
}

function handleEditReview(review: ReviewResponse) {
  openReviewForm(review)
}

function handleDeleteReview(review: ReviewResponse) {
  reviewToDelete.value = review
  isDeleteConfirmOpen.value = true
}

async function confirmDelete() {
  if (!reviewToDelete.value) return

  const success = await deleteReview(reviewToDelete.value.id)
  if (success) {
    isDeleteConfirmOpen.value = false
    reviewToDelete.value = null
    refresh()
  }
}

function isOwnReview(review: ReviewResponse): boolean {
  return !!userId.value && review.user.id === userId.value
}

// Auto-submit pending review after auth redirect.
// Watcher keeps firing until all three sources are truthy, then stops itself.
const stopAutoSubmit = watch([isAuthReady, isAuthenticated, reviewsData], async ([authReady, authenticated, data]) => {
  if (!authReady || !authenticated || !data) return
  stopAutoSubmit()

  if (!hasPendingForClinic(props.clinicId)) return
  if (hasUserReviewed.value || isClinicOwner.value) {
    clearPendingReview()
    return
  }

  const pendingData = getPendingReview()
  if (!pendingData) return

  clearPendingReview()
  localStorage.removeItem('oauth_return_to')

  const result = await createReview({
    rating: pendingData.rating,
    comment: pendingData.comment,
    pros: pendingData.pros,
    cons: pendingData.cons,
  })

  if (result) {
    capture('review_submitted', { clinic_id: props.clinicId, rating: pendingData.rating, has_comment: !!pendingData.comment, is_edit: false, was_pending: true })
    toast.add({ title: 'Hodnocení bylo odesláno', color: 'success' })
    refresh()
  } else {
    toast.add({ title: 'Nepodařilo se odeslat hodnocení', color: 'error' })
  }
})

watch(() => props.triggerOpen, () => {
  if (props.triggerOpen) openReviewForm()
})
</script>

<template>
  <div>
    <!-- Rating Summary Header -->
    <div v-if="total > 0 || !pending" class="mb-6">
      <div v-if="ratedCount > 0" class="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
        <!-- Left: Average + Stars + Count -->
        <div class="flex flex-col items-center shrink-0">
          <span class="text-3xl font-semibold text-highlighted">
            {{ formatAverage(averageRating!) }}
          </span>
          <RatingStars :rating="averageRating" size="md" class="mt-1" />
          <span class="text-sm text-muted mt-1">
            Hodnotilo <strong>{{ ratedCount }}</strong> {{ ratedCount === 1 ? 'zákazník' : ratedCount < 5 ? 'zákazníci' : 'zákazníků' }}
          </span>
          <ClientOnly>
            <UButton
              v-if="isAuthenticated && hasUserReviewed"
              color="neutral"
              variant="outline"
              size="sm"
              icon="i-lucide-pencil"
              label="Upravit hodnocení"
              class="mt-3"
              @click="openReviewForm(userReview!)"
            />
            <UButton
              v-else-if="!isClinicOwner"
              color="primary"
              size="sm"
              icon="i-lucide-pencil"
              label="Napsat recenzi"
              class="mt-3"
              @click="openReviewForm()"
            />
          </ClientOnly>
        </div>

        <!-- Right: Distribution Bars -->
        <div class="w-full max-w-sm space-y-1.5">
          <div v-for="star in [5, 4, 3, 2, 1]" :key="star" class="flex items-center gap-2">
            <span class="text-sm text-muted w-4 text-right shrink-0">{{ star }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="2"
              class="text-yellow-400 shrink-0"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <div class="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                class="h-full bg-primary rounded-full transition-all duration-300"
                :style="{ width: `${distributionPercent(star)}%` }"
              />
            </div>
            <span class="text-xs text-dimmed w-8 text-right shrink-0">{{ distribution[star] }}&times;</span>
          </div>
        </div>
      </div>

      <!-- No ratings yet header -->
      <div v-else class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-highlighted">
          Hodnocení
        </h2>
        <ClientOnly>
          <UButton
            v-if="!isClinicOwner"
            color="primary"
            size="sm"
            icon="i-lucide-pencil"
            label="Napsat recenzi"
            @click="openReviewForm()"
          />
        </ClientOnly>
      </div>
    </div>

    <hr v-if="ratedCount > 0 && reviews.length" class="border-dashed border-default mb-4">

    <!-- Loading -->
    <div v-if="pending && reviews.length === 0" class="space-y-4">
      <USkeleton v-for="i in 3" :key="i" class="h-24" />
    </div>

    <!-- Reviews list -->
    <div v-else-if="reviews.length">
      <div class="divide-y divide-default">
        <ReviewCard
          v-for="review in reviews"
          :key="review.id"
          :review="review"
          :is-own="isOwnReview(review)"
          :show-actions="isAuthenticated"
          @edit="handleEditReview"
          @delete="handleDeleteReview"
        />
      </div>

      <div v-if="totalPages > 1" class="flex justify-center mt-6">
        <UPagination
          v-model:page="page"
          :total="total"
          :items-per-page="limit"
        />
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="text-center py-10 px-4">
      <div class="max-w-sm mx-auto space-y-4">
        <div class="flex justify-center">
          <div class="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <UIcon name="i-lucide-message-square-plus" class="size-7 text-primary" />
          </div>
        </div>
        <h3 class="text-lg font-semibold text-highlighted">Zatím žádná hodnocení</h3>
        <p class="text-muted text-sm">
          87 % zákazníků říká, že recenze jim pomohly vybrat veterináře.
          Podělte se o svou zkušenost a pomozte ostatním.
        </p>
        <ClientOnly>
          <UButton
            v-if="!isClinicOwner"
            color="primary"
            size="lg"
            icon="i-lucide-pencil"
            label="Napsat první hodnocení"
            @click="openReviewForm()"
          />
        </ClientOnly>
      </div>
    </div>

    <!-- Review Form Modal -->
    <ReviewFormModal
      v-model:open="isReviewFormOpen"
      :clinic-id="clinicId"
      :clinic-slug="clinicSlug"
      :clinic-name="clinicName"
      :existing-review="editingReview"
      @success="handleReviewSuccess"
    />

    <!-- Delete Confirmation Modal -->
    <UModal v-model:open="isDeleteConfirmOpen" title="Smazat hodnocení" :ui="{ footer: 'justify-end' }">
      <template #body>
        <p class="text-muted">
          Opravdu chcete smazat své hodnocení? Tuto akci nelze vrátit zpět.
        </p>
      </template>
      <template #footer>
        <UButton
          color="neutral"
          variant="outline"
          label="Zrušit"
          @click="isDeleteConfirmOpen = false"
        />
        <UButton
          color="error"
          label="Smazat"
          :loading="isDeleting"
          @click="confirmDelete"
        />
      </template>
    </UModal>
  </div>
</template>
